/**
 * 問題の並びのサイドバー (ProblemSidebar) の済みの印を、このブラウザの localStorage から読む。サーバーにも DB にも触らない。
 *
 * - コーディング面接の問題は、「例で確かめる」で例が全部通った問題の slug と時刻を残す。それまで残していたのは書きかけのコード (coding-interview-draft) だけで、
 *   通ったかどうかはどこにも残っていなかった
 * - 標準入出力のコーディングテストの練習は、隠れたテストを全部通したことのある問題 (coding-test-progress の solved) を済みとして読む。新しい記録は作らない
 * - 技術面接の問いは、自分で採点した記録 (interview-answer-log) の「言えた」を済みとして読む。新しい記録は作らない
 *
 * SSR の HTML には印を出さず (サーバーは端末の記録を知らない)、描いたあとに読む。同じ端末の別の頁で更新されたときは、印も更新する
 */
import { useEffect, useState } from "react";
import { readAllProgress } from "@/lib/coding-test-progress";
import { type InterviewAnswerLog, browserStorage, readAnswerLog } from "@/lib/interview-answer-log";

export const CODING_PASSED_KEY = "yumesaku:coding-passed:v1";

/** 同じタブの中で記録が変わったことを知らせる (storage イベントは別のタブにしか届かない) */
export const PROGRESS_EVENT = "yumesaku:problem-progress";

type StorageLike = Pick<Storage, "getItem" | "setItem">;

/** slug から、通った時刻 (ms) への対応。壊れた値は捨て、読めた分だけを返す */
export function parseCodingPassed(raw: string | null): Record<string, number> {
  if (!raw) return {};
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return {};
  }
  if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) return {};
  const log: Record<string, number> = {};
  for (const [slug, at] of Object.entries(parsed)) {
    if (typeof at === "number" && Number.isFinite(at)) log[slug] = at;
  }
  return log;
}

export function readCodingPassed(storage: StorageLike | null | undefined): ReadonlySet<string> {
  if (!storage) return new Set();
  try {
    return new Set(Object.keys(parseCodingPassed(storage.getItem(CODING_PASSED_KEY))));
  } catch {
    return new Set();
  }
}

/** 例が全部通った問題を残す。残せたら true。使えない端末 (保存を止めた設定、容量の上限) では何も起きない */
export function markCodingPassed(storage: StorageLike | null | undefined, slug: string, at: number): boolean {
  if (!storage) return false;
  try {
    const next = { ...parseCodingPassed(storage.getItem(CODING_PASSED_KEY)), [slug]: at };
    storage.setItem(CODING_PASSED_KEY, JSON.stringify(next));
    return true;
  } catch {
    return false;
  }
}

/** 標準入出力の練習で、一度でも隠れたテストを全部通した問題の slug */
export function readCodingTestSolved(storage: Parameters<typeof readAllProgress>[0]): ReadonlySet<string> {
  return new Set(
    Object.entries(readAllProgress(storage)).flatMap(([slug, progress]) => (progress.solved ? [slug] : [])),
  );
}

/** 「言えた」と採点した問いの slug */
export function saidSlugs(log: InterviewAnswerLog): ReadonlySet<string> {
  return new Set(Object.entries(log).flatMap(([slug, record]) => (record.grade === "said" ? [slug] : [])));
}

export function notifyProgressChanged() {
  if (typeof window !== "undefined") window.dispatchEvent(new Event(PROGRESS_EVENT));
}

/** 記録を読んで、変わったら読み直す。最初の描画 (SSR と水和) は空の集合 */
function useProgress(read: () => ReadonlySet<string>): ReadonlySet<string> {
  const [slugs, setSlugs] = useState<ReadonlySet<string>>(() => new Set());
  useEffect(() => {
    const sync = () => setSlugs(read());
    sync();
    window.addEventListener("storage", sync);
    window.addEventListener(PROGRESS_EVENT, sync);
    return () => {
      window.removeEventListener("storage", sync);
      window.removeEventListener(PROGRESS_EVENT, sync);
    };
    // read は呼ぶ側が毎回同じ物を渡す (モジュールの関数)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return slugs;
}

const readCoding = () => readCodingPassed(browserStorage());
const readCodingTest = () => readCodingTestSolved(browserStorage());
const readInterview = () => {
  const state = readAnswerLog(browserStorage());
  return state.status === "ready" ? saidSlugs(state.log) : new Set<string>();
};

export function useCodingPassed() {
  return useProgress(readCoding);
}

export function useCodingTestSolved() {
  return useProgress(readCodingTest);
}

export function useInterviewSaid() {
  return useProgress(readInterview);
}
